/**
 * GIUSP (Gas Industry Unsafe Situations Procedure) Engine — Edition 8.1
 *
 * Classifies unsafe gas situations and determines the required procedural
 * response for Gas Safe registered engineers in Great Britain.
 *
 * Classification taxonomy (GIUSP Ed 8.1):
 *  - Immediately Dangerous (ID)     → isolate, Danger Do Not Use notice, report to ESP
 *  - At Risk (AR)                   → turn off with consent, warning notice
 *  - Not to Current Standards (NCS) → advisory notice, non-urgent remediation
 *
 * Each classification carries statutory action flags used to auto-generate
 * the Warning Notice (Task 2.2) and the "Do Not Use" Danger Notice.
 */

/**
 * Enum of GIUSP classifications.
 */
export const GIUSP_CLASSIFICATION = {
  IMMEDIATELY_DANGEROUS: 'immediately-dangerous',
  AT_RISK: 'at-risk',
  NOT_TO_CURRENT_STANDARDS: 'not-to-current-standards'
};

/**
 * Enum of required procedural actions.
 */
export const GIUSP_ACTION = {
  ISOLATE_AND_LEAVE: 'isolate-and-leave',
  TURN_OFF_WITH_CONSENT: 'turn-off-with-consent',
  CAP_OR_DISCONNECT: 'cap-or-disconnect',
  REPORT_TO_ESP: 'report-to-esp',
  WARNING_NOTICE: 'warning-notice',
  DANGER_DO_NOT_USE_NOTICE: 'danger-do-not-use-notice'
};

/**
 * GIUSP unsafe-situation predicate rules.
 */
const GIUSP_RULES = [
  {
    id: 'GAS_ESCAPE_PRESENT',
    classification: GIUSP_CLASSIFICATION.IMMEDIATELY_DANGEROUS,
    description: 'Presence of gas escape or likelihood of gas escape',
    actions: [
      GIUSP_ACTION.CAP_OR_DISCONNECT,
      GIUSP_ACTION.REPORT_TO_ESP,
      GIUSP_ACTION.DANGER_DO_NOT_USE_NOTICE
    ],
    guidance:
      'Treat as Immediately Dangerous. Isolate the gas supply (ECV) if safe to do so, do not relight appliances, apply the Danger Do Not Use notice, and escalate the escape to the Gas Emergency Service Provider (0800 111 999).'
  },
  {
    id: 'ROOM_SEALED_SPILLAGE',
    classification: GIUSP_CLASSIFICATION.IMMEDIATELY_DANGEROUS,
    description: 'Spillage, flue flow or ventilation test fails on a room-sealed appliance',
    actions: [
      GIUSP_ACTION.CAP_OR_DISCONNECT,
      GIUSP_ACTION.REPORT_TO_ESP,
      GIUSP_ACTION.DANGER_DO_NOT_USE_NOTICE
    ],
    guidance:
      'A failed flue flow / spillage test is Immediately Dangerous. Isolate the appliance, do not relight, fit a Danger Do Not Use notice and report it as an uncontrolled combustion risk.'
  },
  {
    id: 'CARBON_MONOXIDE_RISK',
    classification: GIUSP_CLASSIFICATION.IMMEDIATELY_DANGEROUS,
    description: 'Evidence of combustion products entering the room, or elevated CO readings',
    actions: [
      GIUSP_ACTION.CAP_OR_DISCONNECT,
      GIUSP_ACTION.DANGER_DO_NOT_USE_NOTICE
    ],
    guidance:
      'Immediately Dangerous — elevated CO or spillage poses a live fume risk. Isolate the appliance, apply a Danger Do Not Use notice, ventilate and report the installation.'
  },
  {
    id: 'APPLIANCE_DAMAGED',
    classification: GIUSP_CLASSIFICATION.AT_RISK,
    description: 'Damaged appliance, flue or gas supply pipe condition that could lead to unsafe operation',
    actions: [
      GIUSP_ACTION.TURN_OFF_WITH_CONSENT,
      GIUSP_ACTION.WARNING_NOTICE,
      GIUSP_ACTION.CAP_OR_DISCONNECT
    ],
    guidance:
      'At Risk. Do not run the appliance. Turn off with consumer consent, complete an AR warning notice and list the required remediation to bring the installation to a safe standard.'
  },
  {
    id: 'GAS_TIGHTNESS_FAIL',
    classification: GIUSP_CLASSIFICATION.AT_RISK,
    description: 'Installation tightness test fails, or evidence of pipe leakage',
    actions: [
      GIUSP_ACTION.CAP_OR_DISCONNECT,
      GIUSP_ACTION.WARNING_NOTICE,
      GIUSP_ACTION.REPORT_TO_ESP
    ],
    guidance:
      'At Risk. Fault on installation tightness. Turn off and, with consumer consent, cap or isolate at the meter to make the installation safe. Issue a warning notice.'
  },
  {
    id: 'VENTILATION_INADEQUATE',
    classification: GIUSP_CLASSIFICATION.AT_RISK,
    description: 'Inadequate permanent ventilation for safe operation of appliance(s)',
    actions: [
      GIUSP_ACTION.TURN_OFF_WITH_CONSENT,
      GIUSP_ACTION.WARNING_NOTICE
    ],
    guidance:
      'At Risk due to ventilation failure. With consumer consent, turn off and post a warning notice to warn against further use.'
  },
  {
    id: 'FLUE_DEFECT_MINOR',
    classification: GIUSP_CLASSIFICATION.NOT_TO_CURRENT_STANDARDS,
    description: 'Minor flue or appliance defect not posing immediate danger',
    actions: [GIUSP_ACTION.WARNING_NOTICE],
    guidance:
      'Not to Current Standards — non-urgent defect. Record on the warning notice and schedule work to bring the installation up to current standards.'
  }
];

/**
 * Build a consistent classification result object.
 */
const buildResult = (classification, rule, message) => ({
  classification,
  severity:
    classification === GIUSP_CLASSIFICATION.IMMEDIATELY_DANGEROUS
      ? 'critical'
      : classification === GIUSP_CLASSIFICATION.AT_RISK
        ? 'warning'
        : 'info',
  label:
    classification === GIUSP_CLASSIFICATION.IMMEDIATELY_DANGEROUS
      ? 'Immediately Dangerous'
      : classification === GIUSP_CLASSIFICATION.AT_RISK
        ? 'At Risk'
        : 'Not to Current Standards',
  code: rule.id,
  description: rule.description,
  actions: rule.actions,
  guidance: rule.guidance,
  message
});

/**
 * Classify an unsafe situation from test/flags inputs.
 *
 * @param {Object} flags
 * @param {boolean} flags.gasEscape - Trace/odour of gas or active leak
 * @param {boolean} flags.spillage - Flue flow / spillage test failed
 * @param {boolean} flags.highCo - Elevated CO concentration
 * @param {boolean} flags.applianceDamaged - Physical damage to appliance or flue
 * @param {boolean} flags.tightnessFail - Tightness test failed
 * @param {boolean} flags.poorVentilation - Inadequate ventilation
 * @returns {Object} { classification, severity, label, code, description, actions, guidance, message }
 */
export const classifyUnsafeSituation = ({
  gasEscape = false,
  spillage = false,
  highCo = false,
  applianceDamaged = false,
  tightnessFail = false,
  poorVentilation = false
}) => {
  // Most severe first: Immediately Dangerous
  if (gasEscape || spillage || highCo) {
    const rule = gasEscape
      ? GIUSP_RULES[0]
      : spillage
        ? GIUSP_RULES[1]
        : GIUSP_RULES[2];
    return buildResult(
      GIUSP_CLASSIFICATION.IMMEDIATELY_DANGEROUS,
      rule,
      [
        'Isolate the gas supply immediately (if safe).',
        'Cap or disconnect the appliance.',
        'Apply a Danger Do Not Use notice.',
        'Report to the Gas Emergency Service Provider (0800 111 999).'
      ]
    );
  }

  // At Risk
  if (applianceDamaged || tightnessFail || poorVentilation) {
    const rule = applianceDamaged
      ? GIUSP_RULES[3]
      : tightnessFail
        ? GIUSP_RULES[4]
        : GIUSP_RULES[5];
    return buildResult(
      GIUSP_CLASSIFICATION.AT_RISK,
      rule,
      [
        'Turn off the appliance with consumer consent.',
        'Apply an At Risk warning notice.',
        'Record the appliance as not to current standards on the form.'
      ]
    );
  }

  // Not to current standards (advisory only)
  return buildResult(
    GIUSP_CLASSIFICATION.NOT_TO_CURRENT_STANDARDS,
    GIUSP_RULES[6],
    [
      'No immediate danger identified.',
      'Issue an advisory notice to bring the installation up to current standards.'
    ]
  );
};

export default {
  GIUSP_CLASSIFICATION,
  GIUSP_ACTION,
  GIUSP_RULES,
  classifyUnsafeSituation
};